import React from "react";
import { IconButton } from "../../atoms/IconButton";
import { Tooltip } from "../../atoms/Tooltip";
import { SOON_TOOLTIP } from "../../atoms/SoonBadge";
import { Divider, Dot, Group, ItemSlot, Rail, Shortcut, SoonTag, Spacer } from "./IconRail.styles";
import type { IconRailEdge, IconRailItem, IconRailProps } from "./IconRail.types";

/** 18px glyph in a 40px button (design). */
const ICON_SIZE = 18;

const TOOLTIP_SIDE: Record<IconRailEdge, "left" | "right"> = { left: "right", right: "left" };

/**
 * A vertical rail of icon buttons for a page edge: grouped items, dividers, a top and a bottom slot.
 * Stateless: the caller owns which item is selected. States follow the design: default, hover,
 * selected, placing, keyboard focus, disabled, and disabled with a Coming soon tag.
 */
export const IconRail: React.FC<IconRailProps> = ({
  ariaLabel,
  groups,
  edge = "left",
  horizontal = false,
  dividers = true,
  topSlot,
  bottomSlot,
  soonLabel = SOON_TOOLTIP,
  className,
}) => {
  const renderItem = (item: IconRailItem) => {
    const inert = Boolean(item.disabled || item.soon);
    const tooltipText = item.soon
      ? item.label
      : item.disabled
        ? (item.disabledReason ?? item.tooltip ?? item.label)
        : (item.tooltip ?? item.label);
    const trailing = item.soon ? (
      <SoonTag label={soonLabel} />
    ) : item.shortcut && !inert ? (
      <Shortcut>{item.shortcut}</Shortcut>
    ) : undefined;
    const selected = !inert && Boolean(item.selected);
    const placing = !inert && Boolean(item.placing);
    const hasToggleState = item.selected !== undefined || item.placing !== undefined;
    return (
      <Tooltip key={item.id} label={tooltipText} position={horizontal ? (edge === "left" ? "bottom" : "top") : TOOLTIP_SIDE[edge]} arrow={false} trailing={trailing}>
        <ItemSlot>
          <IconButton
            variant="rail"
            size="xl"
            iconSize={ICON_SIZE}
            icon={item.icon}
            aria-label={item.soon ? `${item.label}, ${soonLabel.toLowerCase()}` : item.label}
            aria-disabled={inert}
            tabIndex={inert ? -1 : undefined}
            aria-pressed={hasToggleState && !inert ? selected || placing : undefined}
            title={null}
            $active={selected}
            $placing={placing}
            onClick={(event) => {
              // A mouse click leaves focus on the button; the rail outlives the navigation it triggers, so the
              // :focus-within tooltip would stay open. Keyboard activation (detail 0) keeps its focus.
              if (event.detail > 0) event.currentTarget.blur();
              item.onClick?.(event);
            }}
          />
          {item.indicator && !inert ? <Dot $tone={item.indicator} aria-hidden /> : null}
        </ItemSlot>
      </Tooltip>
    );
  };

  const visibleGroups = groups.filter((group) => group.items.length > 0);

  return (
    <Rail aria-label={ariaLabel} $edge={edge} $horizontal={horizontal} className={className}>
      {topSlot}
      {dividers && topSlot && visibleGroups.length > 0 ? <Divider aria-hidden /> : null}
      {visibleGroups.map((group, index) => (
        <React.Fragment key={group.id}>
          {dividers && index > 0 ? <Divider aria-hidden /> : null}
          <Group>{group.items.map(renderItem)}</Group>
        </React.Fragment>
      ))}
      {bottomSlot ? (
        <>
          <Spacer />
          {dividers ? <Divider $tight aria-hidden /> : null}
          {bottomSlot}
        </>
      ) : null}
    </Rail>
  );
};

export default IconRail;
